/**
 * Trims demo chart data down to the most recent months on phone-sized screens.
 *
 * Phone layouts squeeze years of points into a few hundred pixels, so the demo serves
 * a shorter window instead. This lives here rather than in the charts because the two
 * pages involved use three different charting setups — and the Pricing & Forecast
 * charts have no date filter at all — so trimming the data is the only single place
 * that covers them.
 *
 * Desktop is untouched, the real app is untouched, and any file not listed below is
 * served exactly as captured.
 */

import { isDemoMode } from './demoState';

const MONTHS_SHOWN = 8;

/** Phone-sized only. Matches the width where the forecast page drops to one column. */
const SMALL_SCREEN_MAX_WIDTH = 768;

interface TrimRule {
  /** The field holding each row's date. The name differs per file. */
  dateField: string;
  /** How far back to keep. Defaults to MONTHS_SHOWN. */
  monthsOfHistory?: number;
  /**
   * For series that run into the future, like a forecast. Measures back from today
   * instead of the newest row, so every future row is kept and only old history goes.
   * Measuring from the newest row would cut the forecast off at its own far end.
   */
  keepsFuture?: boolean;
}

/**
 * Add a file here to trim it. Anything not listed is served exactly as captured.
 *
 * Deliberately absent: forecastCommentary.json, whose only date is an edit timestamp
 * rather than a data date.
 */
const TRIMMED_FILES: Record<string, TrimRule> = {
  // Fair Price & Forecast page. These charts have no range slider, so shortening the
  // data is the only way to narrow them. Market Intel is absent on purpose — those
  // charts keep their full data and just open at a 1 year window instead.
  'fairPriceData.json': { dateField: 'Date' },
  'forecastData.json': {
    dateField: 'Date_Quarterly',
    monthsOfHistory: 6,
    keepsFuture: true,
  },
};

/** True when the demo is running on a phone-sized screen. */
export function isDemoOnPhone(): boolean {
  return (
    isDemoMode() &&
    typeof window !== 'undefined' &&
    window.innerWidth <= SMALL_SCREEN_MAX_WIDTH
  );
}

function toTime(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const time = new Date(value).getTime();
  return isNaN(time) ? null : time;
}

/**
 * Returns the response trimmed to the last few months, or unchanged when it isn't a
 * small screen, isn't a listed file, or isn't shaped the way we expect.
 */
export function trimToRecentMonths(
  params: Record<string, unknown> | null | undefined,
  data: unknown,
): unknown {
  if (typeof window === 'undefined' || window.innerWidth > SMALL_SCREEN_MAX_WIDTH) {
    return data;
  }

  const filename = typeof params?.filename === 'string' ? params.filename : null;
  const rule = filename ? TRIMMED_FILES[filename] : undefined;
  if (!rule) return data;
  const { dateField, monthsOfHistory = MONTHS_SHOWN, keepsFuture = false } = rule;

  // Cache responses arrive as JSON strings.
  if (typeof data !== 'string') return data;
  let rows: unknown;
  try {
    rows = JSON.parse(data);
  } catch {
    return data;
  }
  if (!Array.isArray(rows)) return data;

  const times = rows
    .map((row) => toTime((row as Record<string, unknown>)?.[dateField]))
    .filter((time): time is number => time !== null);
  if (!times.length) return data;

  // Normally measured from the newest row rather than today, so the window stays the
  // same size as the snapshot ages. Forward-looking series measure from today instead.
  const cutoff = new Date(keepsFuture ? Date.now() : Math.max(...times));
  cutoff.setMonth(cutoff.getMonth() - monthsOfHistory);

  const trimmed = rows.filter((row) => {
    const time = toTime((row as Record<string, unknown>)?.[dateField]);
    return time === null || time >= cutoff.getTime();
  });

  return JSON.stringify(trimmed);
}
